import React, { useState } from "react";
import { Box, Text, Button, Flex, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router";
import { ForgotPassword } from "../../components/ForgotPassword";
import { ChangePassword } from "../../components/ChangePassword";
import {
    validateEmail,
    validatePhone,
    validatePassword,
} from "../../utils/helper";
import { HelmetComponent } from "../../components/HelmetComponent";

const MotionBox = motion(Box);

export const ForgotPasswordPage = () => {
    const [username, setUsername] = useState("");
    const [countryCode, setCountryCode] = useState("+91");
    const [showCountryCode, setShowCountryCode] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isError, setIsError] = useState(false);
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordAgain, setNewPasswordAgain] = useState("");
    const [isCodeError, setIsCodeError] = useState(false);
    const [isPasswordError, setIsPasswordError] = useState(false);
    const [isRepeatPasswordError, setIsRepeatPasswordError] = useState(false);
    const [isForgotPasswordScreen, setIsForgotPasswordScreen] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [changePasswordErrorMessage, setChangePasswordErrorMessage] =
        useState("");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const onUsernameChange = (e) => {
        setUsername(e.target.value);
        if (isNaN(e.target.value) || e.target.value === "") {
            setShowCountryCode(false);
        } else {
            setShowCountryCode(true);
        }
        if (isNaN(e.target.value) && !validateEmail(e.target.value)) {
            setIsError(true);
            setErrorMessage("Enter a valid email");
        } else if (!isNaN(e.target.value) && !validatePhone(e.target.value)) {
            setIsError(true);
            setErrorMessage("Enter a valid phone number");
        } else {
            setIsError(false);
            setErrorMessage("");
        }
    };
    const onPasswordChange = (e) => {
        if (e.target.name === "new_password") {
            setNewPassword(e.target.value);
            if (!validatePassword(e.target.value)) {
                setIsPasswordError(true);
            } else {
                setIsPasswordError(false);
            }
        }
        if (e.target.name === "new_password_again") {
            setNewPasswordAgain(e.target.value);
            if (
                !validatePassword(e.target.value) ||
                newPassword.trim() !== e.target.value
            ) {
                setIsRepeatPasswordError(true);
            } else {
                setIsRepeatPasswordError(false);
            }
        }
    };
    const onResetPasswordSubmit = async (e) => {
        e.preventDefault();
        if (!validatePassword(newPassword)) {
            setIsPasswordError(true);
            return true;
        }
        if (
            !validatePassword(newPasswordAgain) ||
            newPassword !== newPasswordAgain
        ) {
            setIsRepeatPasswordError(true);
            return true;
        }
        try {
            setIsLoading(true);
            const userName = isNaN(username) ? username : `${countryCode}${username}`;
            await Auth.forgotPasswordSubmit(userName, code, newPassword);
            setIsLoading(false);
            navigate("/");
        } catch (err) {
            setIsLoading(false);
            setIsCodeError(true);
            setChangePasswordErrorMessage(err.message);
        }
    };
    const onForgotPasswordSubmit = async (e) => {
        if (e) e.preventDefault();
        if (isNaN(username) && !validateEmail(username)) {
            setIsError(true);
            setErrorMessage("Enter a valid email");
            return true;
        }
        if (!isNaN(username) && !validatePhone(username)) {
            setIsError(true);
            setErrorMessage("Enter a valid phone number");
            return true;
        }
        try {
            const userName = isNaN(username) ? username : `${countryCode}${username}`;
            setIsLoading(true);
            await Auth.forgotPassword(userName);
            setIsLoading(false);
            setIsForgotPasswordScreen(false);
        } catch (err) {
            setIsLoading(false);
        }
    };
    return (
        <>
            <HelmetComponent title={`Forgot Password - Edshopify`}/>
            <Flex
                sx={{
                    minHeight: "100vh",
                    alignItems: {base: "flex-start", sm: "center"},
                    padding: "20px",
                    background: "rgb(249 250 251)"
                }}
            >
                <Box sx={{width: "100%"}}>
                    <Text className='logo' sx={{margin: "0 0 20px", textAlign: "center"}}>EdShopify</Text>
                    <MotionBox
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        maxW="md"
                        mx="auto"
                        p={{base: 0, sm: 6}}
                        borderWidth={{base: "none", sm: "1"}}
                        borderRadius={{base: "none", sm: "lg"}}
                        shadow={{base: "none", sm: "lg"}}
                        sx={{
                            minWidth: {base: "100%", sm: "400px"}
                        }}
                    >
                        {isForgotPasswordScreen ? (
                            <ForgotPassword
                                username={username}
                                errorMessage={errorMessage}
                                showCountryCode={showCountryCode}
                                isError={isError}
                                onUsernameChange={onUsernameChange}
                                onForgotPasswordSubmit={onForgotPasswordSubmit}
                                codeChange={(country_code) => setCountryCode(country_code)}
                                isLoading={isLoading}
                            />
                        ) : (
                            <ChangePassword
                                code={code}
                                newPassword={newPassword}
                                newPasswordAgain={newPasswordAgain}
                                changePasswordErrorMessage={changePasswordErrorMessage}
                                isCodeError={isCodeError}
                                isPasswordError={isPasswordError}
                                isRepeatPasswordError={isRepeatPasswordError}
                                onPasswordChange={onPasswordChange}
                                onCodeChange={(e) => setCode(e.target.value)}
                                onResetPasswordSubmit={onResetPasswordSubmit}
                                showPassword={showPassword}
                                toggleShowPassword={() => setShowPassword(!showPassword)}
                                isLoading={isLoading}
                            />
                        )}
                        <VStack spacing={5} mt={5}>
                            {isCodeError && (
                                <Button
                                    colorScheme="blue"
                                    variant="ghost"
                                    onClick={() => {
                                        setIsCodeError(false);
                                        onForgotPasswordSubmit();
                                    }}
                                >
                                    Resend code again
                                </Button>
                            )}
                            <Button
                                variant='outline'
                                colorScheme="blue"
                                disabled={isLoading}
                                onClick={() => navigate('/')}
                                sx={{
                                    width: "100%"
                                }}
                            >Back to login</Button>
                        </VStack>
                    </MotionBox>
                </Box>
            </Flex>
        </>
    )
}
